import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Globe, Search, Save, ChevronDown, ChevronRight,
  CheckCircle2, AlertCircle, Eye, EyeOff, FileText,
  RefreshCw, X, Plus
} from "lucide-react";
import { toast } from "sonner";

const CHANGEFREQ_OPTIONS = ["always", "hourly", "daily", "weekly", "monthly", "yearly", "never"];

const SeoPageManager = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPath, setExpandedPath] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/seo/pages`, { withCredentials: true });
      setPages(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      toast.error("Failed to load SEO pages");
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (page) => {
    if (expandedPath === page.path) {
      setExpandedPath(null);
      return;
    }
    setExpandedPath(page.path);
    setEditData({
      path: page.path,
      page_name: page.page_name || "",
      seo_title: page.seo_title || "",
      seo_description: page.seo_description || "",
      canonical_url: page.canonical_url || "",
      robots_index: page.robots_index !== false,
      robots_follow: page.robots_follow !== false,
      focus_keyword: page.focus_keyword || "",
      secondary_keywords: (page.secondary_keywords || []).join(", "),
      og_title: page.og_title || "",
      og_description: page.og_description || "",
      og_image: page.og_image || "",
      twitter_title: page.twitter_title || "",
      twitter_description: page.twitter_description || "",
      twitter_image: page.twitter_image || "",
      schema_markup: page.schema_markup || "",
      sitemap_include: page.sitemap_include !== false,
      sitemap_priority: page.sitemap_priority ?? 0.5,
      sitemap_changefreq: page.sitemap_changefreq || "weekly",
      publish_status: page.publish_status || "published",
    });
  };

  const update = (key, val) => setEditData(prev => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editData,
        secondary_keywords: editData.secondary_keywords
          ? editData.secondary_keywords.split(",").map(k => k.trim()).filter(Boolean)
          : [],
        sitemap_priority: parseFloat(editData.sitemap_priority) || 0.5,
      };
      await axios.put(`${API}/seo/pages`, payload, { withCredentials: true });
      toast.success(`SEO saved for ${editData.path}`);
      fetchPages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save SEO data");
    } finally {
      setSaving(false);
    }
  };

  const getSeoScore = (page) => {
    let score = 0;
    if (page.seo_title) score++;
    if (page.seo_description) score++;
    if (page.focus_keyword) score++;
    if (page.og_title || page.og_image) score++;
    if (page.canonical_url) score++;
    return score;
  };

  const filtered = pages.filter(p =>
    (p.page_name || p.path || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.path || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="py-12 text-center">
        <RefreshCw className="w-8 h-8 animate-spin text-emerald-600 dark:text-emerald-400 mx-auto" />
        <p className="text-sm text-gray-500 dark:text-[#94A3B8] mt-3 font-plex">Loading SEO pages...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="seo-page-manager">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-gray-900 dark:text-white font-outfit font-bold flex items-center gap-2">
            <Globe className="w-5 h-5 text-emerald-600 dark:text-emerald-400" /> SEO Page Manager
          </h2>
          <p className="text-xs text-gray-500 dark:text-[#94A3B8] font-plex mt-1">
            Manage SEO metadata for all public pages — Home, Blogs, Courses, Legal, and more.
          </p>
        </div>
        <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800 text-xs">
          {pages.length} pages
        </Badge>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-[#52525B]" />
        <Input
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search pages..."
          className="pl-10 bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.08] text-gray-900 dark:text-white text-sm font-plex h-9"
        />
      </div>

      {/* Page List */}
      <div className="space-y-2">
        {filtered.map(page => {
          const score = getSeoScore(page);
          const isExpanded = expandedPath === page.path;
          const scoreColor = score >= 4 ? "text-emerald-600 dark:text-emerald-400" : score >= 2 ? "text-amber-500" : "text-rose-500";
          const scoreBg = score >= 4 ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800" : score >= 2 ? "bg-amber-50 text-amber-700 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800" : "bg-rose-50 text-rose-700 border border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800";

          return (
            <Card key={page.path} className={`bg-gray-50 dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] overflow-hidden transition-all ${isExpanded ? 'border-emerald-500/40 ring-1 ring-emerald-500/20 shadow-xs' : ''}`}>
              {/* Row Header */}
              <button
                onClick={() => openEditor(page)}
                className="w-full p-4 flex items-center justify-between hover:bg-gray-50 dark:bg-white/[0.01] transition-colors text-left"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-8 h-8 rounded flex items-center justify-center ${scoreBg}`}>
                    {score >= 4 ? <CheckCircle2 className={`w-4 h-4 ${scoreColor}`} /> : <AlertCircle className={`w-4 h-4 ${scoreColor}`} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-white font-outfit truncate">{page.page_name || page.path}</p>
                    <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">{page.path}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {page.source && (
                    <Badge className="bg-gray-100 dark:bg-white/5 text-gray-500 dark:text-[#94A3B8] border-0 text-[9px] capitalize">{page.source}</Badge>
                  )}
                  <Badge className={`${scoreBg} ${scoreColor} border-0 text-[10px] font-jetbrains`}>
                    {score}/5
                  </Badge>
                  {page.publish_status === "draft" && (
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800 text-[9px]">Draft</Badge>
                  )}
                  {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500 dark:text-[#94A3B8]" /> : <ChevronRight className="w-4 h-4 text-gray-400 dark:text-[#52525B]" />}
                </div>
              </button>

              {/* Expanded Editor */}
              {isExpanded && (
                <div className="p-5 pt-0 border-t border-gray-100 dark:border-white/[0.04] space-y-5">
                  {/* Basic SEO */}
                  <div className="space-y-3 pt-4">
                    <h4 className="text-xs text-emerald-600 dark:text-emerald-400 font-jetbrains uppercase font-bold flex items-center gap-1.5">
                      <FileText className="w-3.5 h-3.5" /> Basic SEO
                    </h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">SEO Title</Label>
                        <Input value={editData.seo_title} onChange={e => update("seo_title", e.target.value)}
                          className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm" placeholder="Page title for search engines" />
                        <p className="text-[10px] text-gray-400 dark:text-[#52525B] mt-0.5 font-jetbrains">{(editData.seo_title || "").length}/60 characters</p>
                      </div>
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Canonical URL</Label>
                        <Input value={editData.canonical_url} onChange={e => update("canonical_url", e.target.value)}
                          className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm" placeholder="https://jamadhan.com/page" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Meta Description</Label>
                      <Textarea value={editData.seo_description} onChange={e => update("seo_description", e.target.value)}
                        className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm min-h-[60px]" placeholder="Compelling description for search results..." />
                      <p className="text-[10px] text-gray-400 dark:text-[#52525B] mt-0.5 font-jetbrains">{(editData.seo_description || "").length}/160 characters</p>
                    </div>
                  </div>

                  {/* Keywords */}
                  <div className="space-y-3">
                    <h4 className="text-xs text-[#FFCC00] font-jetbrains uppercase font-bold flex items-center gap-1.5">
                      <Search className="w-3.5 h-3.5" /> Keywords
                    </h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Focus Keyword</Label>
                        <Input value={editData.focus_keyword} onChange={e => update("focus_keyword", e.target.value)}
                          className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm" placeholder="Main target keyword" />
                      </div>
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Secondary Keywords</Label>
                        <Input value={editData.secondary_keywords} onChange={e => update("secondary_keywords", e.target.value)}
                          className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm" placeholder="keyword1, keyword2, keyword3" />
                        <p className="text-[10px] text-gray-400 dark:text-[#52525B] mt-0.5 font-jetbrains">Comma-separated</p>
                      </div>
                    </div>
                  </div>

                  {/* Robots & Sitemap */}
                  <div className="space-y-3">
                    <h4 className="text-xs text-emerald-600 dark:text-emerald-400 font-mono uppercase font-bold flex items-center gap-1.5">
                      <Globe className="w-3.5 h-3.5" /> Robots & Sitemap
                    </h4>
                    <div className="grid md:grid-cols-4 gap-3">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={editData.robots_index} onChange={e => update("robots_index", e.target.checked)}
                          className="rounded border-gray-200 dark:border-white/10 bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] text-emerald-600 focus:ring-emerald-500" />
                        <Label className="text-gray-900 dark:text-white text-xs cursor-pointer">Index</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={editData.robots_follow} onChange={e => update("robots_follow", e.target.checked)}
                          className="rounded border-gray-200 dark:border-white/10 bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] text-emerald-600 focus:ring-emerald-500" />
                        <Label className="text-gray-900 dark:text-white text-xs cursor-pointer">Follow</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={editData.sitemap_include} onChange={e => update("sitemap_include", e.target.checked)}
                          className="rounded border-gray-200 dark:border-white/10 bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] text-emerald-600 focus:ring-emerald-500" />
                        <Label className="text-gray-900 dark:text-white text-xs cursor-pointer">Include in Sitemap</Label>
                      </div>
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Publish Status</Label>
                        <select value={editData.publish_status} onChange={e => update("publish_status", e.target.value)}
                          className="mt-1 w-full bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border border-gray-200 dark:border-white/[0.06] rounded-md px-2 py-1.5 text-gray-900 dark:text-white text-xs focus:outline-none focus:border-emerald-500">
                          <option value="published">Published</option>
                          <option value="draft">Draft</option>
                        </select>
                      </div>
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Sitemap Priority</Label>
                        <Input type="number" min="0" max="1" step="0.1" value={editData.sitemap_priority}
                          onChange={e => update("sitemap_priority", e.target.value)}
                          className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm font-jetbrains" />
                      </div>
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Change Frequency</Label>
                        <select value={editData.sitemap_changefreq} onChange={e => update("sitemap_changefreq", e.target.value)}
                          className="mt-1 w-full bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border border-gray-200 dark:border-white/[0.06] rounded-md px-2 py-1.5 text-gray-900 dark:text-white text-xs focus:outline-none focus:border-emerald-500">
                          {CHANGEFREQ_OPTIONS.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Open Graph */}
                  <div className="space-y-3">
                    <h4 className="text-xs text-[#A855F7] font-jetbrains uppercase font-bold">Open Graph Tags</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">OG Title</Label>
                        <Input value={editData.og_title} onChange={e => update("og_title", e.target.value)}
                          className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm" placeholder="Open Graph title" />
                      </div>
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">OG Image URL</Label>
                        <Input value={editData.og_image} onChange={e => update("og_image", e.target.value)}
                          className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm" placeholder="https://..." />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">OG Description</Label>
                      <Textarea value={editData.og_description} onChange={e => update("og_description", e.target.value)}
                        className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm min-h-[50px]" placeholder="Description for social shares" />
                    </div>
                  </div>

                  {/* Twitter Tags */}
                  <div className="space-y-3">
                    <h4 className="text-xs text-[#1DA1F2] font-jetbrains uppercase font-bold">Twitter Tags</h4>
                    <div className="grid md:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Twitter Title</Label>
                        <Input value={editData.twitter_title} onChange={e => update("twitter_title", e.target.value)}
                          className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm" placeholder="Twitter card title" />
                      </div>
                      <div>
                        <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Twitter Image URL</Label>
                        <Input value={editData.twitter_image} onChange={e => update("twitter_image", e.target.value)}
                          className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm" placeholder="https://..." />
                      </div>
                    </div>
                    <div>
                      <Label className="text-gray-500 dark:text-[#A1A1AA] text-xs">Twitter Description</Label>
                      <Textarea value={editData.twitter_description} onChange={e => update("twitter_description", e.target.value)}
                        className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm min-h-[50px]" placeholder="Description for Twitter cards" />
                    </div>
                  </div>

                  {/* Schema */}
                  <div className="space-y-3">
                    <h4 className="text-xs text-[#F59E0B] font-jetbrains uppercase font-bold">JSON-LD Schema</h4>
                    <Textarea value={editData.schema_markup} onChange={e => update("schema_markup", e.target.value)}
                      className="bg-white dark:bg-[#F3F4F6] dark:bg-[#050505] border-gray-200 dark:border-white/[0.06] text-gray-900 dark:text-white mt-1 text-sm font-jetbrains min-h-[100px]"
                      placeholder='{"@context":"https://schema.org","@type":"WebPage",...}' />
                    <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">Paste valid JSON-LD. Will be injected as a script tag.</p>
                  </div>

                  {/* Save */}
                  <div className="flex items-center gap-3 pt-2 border-t border-gray-100 dark:border-white/[0.04]">
                    <Button onClick={handleSave} disabled={saving}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-xs rounded-xl transition-all text-xs h-8 px-4">
                      <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving..." : "Save SEO"}
                    </Button>
                    <Button variant="ghost" onClick={() => setExpandedPath(null)}
                      className="text-gray-400 dark:text-[#52525B] hover:text-gray-900 dark:text-white text-xs h-8">
                      Cancel
                    </Button>
                    <div className="flex-1" />
                    <p className="text-[10px] text-gray-400 dark:text-[#52525B] font-jetbrains">
                      {editData.path}
                    </p>
                  </div>
                </div>
              )}
            </Card>
          );
        })}

        {filtered.length === 0 && (
          <Card className="bg-gray-50 dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06]">
            <CardContent className="p-8 text-center">
              <p className="text-gray-500 dark:text-[#94A3B8] font-plex text-sm">No pages found matching "{searchTerm}"</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SeoPageManager;
